"use client";

import { useState } from "react";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  type ProjectedPoint,
  projectPoints,
  type SketchPoint,
} from "@/lib/tool-kit/chem-reference/vsepr";

export interface SketchStrings {
  sketchHeading: string;
  rotateLabel: string;
  sketchLabel: string;
  sketchKey: string;
}

const SIZE = 240;
const CENTER = SIZE / 2;
const REACH = 78;
const TILT = 22;
const START_AZIMUTH = 25;
const FULL_TURN = 359;

function Bond({ point }: { point: ProjectedPoint }) {
  return (
    <line
      className="stroke-foreground"
      opacity={point.depth < 0 ? 0.55 : 1}
      strokeLinecap="round"
      strokeWidth={3}
      x1={CENTER}
      x2={CENTER + point.x * REACH}
      y1={CENTER}
      y2={CENTER + point.y * REACH}
    />
  );
}

function Atom({ point }: { point: ProjectedPoint }) {
  const radius = 13 + point.depth * 3;
  return (
    <g opacity={point.depth < 0 ? 0.75 : 1}>
      <circle
        className="fill-primary stroke-background"
        cx={CENTER + point.x * REACH}
        cy={CENTER + point.y * REACH}
        r={radius}
        strokeWidth={2}
      />
      <text
        className="fill-primary-foreground font-semibold"
        dominantBaseline="central"
        fontSize={13}
        textAnchor="middle"
        x={CENTER + point.x * REACH}
        y={CENTER + point.y * REACH}
      >
        X
      </text>
    </g>
  );
}

function LonePair({ point }: { point: ProjectedPoint }) {
  const length = Math.hypot(point.x, point.y);
  const angle = (Math.atan2(point.y, point.x) * 180) / Math.PI;
  const cx = CENTER + point.x * REACH * 0.62;
  const cy = CENTER + point.y * REACH * 0.62;
  // A lobe pointing at the viewer is seen end-on, so it shrinks to a round blob.
  const ry = 13 + (1 - Math.min(length, 1)) * 8;
  return (
    <g opacity={point.depth < 0 ? 0.6 : 1}>
      <ellipse
        className="fill-primary/15 stroke-primary"
        cx={cx}
        cy={cy}
        rx={26}
        ry={ry}
        strokeDasharray="4 3"
        strokeWidth={1.5}
        transform={`rotate(${angle} ${cx} ${cy})`}
      />
      <circle className="fill-primary" cx={cx - 5} cy={cy} r={3} />
      <circle className="fill-primary" cx={cx + 5} cy={cy} r={3} />
    </g>
  );
}

function Items({ points }: { points: readonly ProjectedPoint[] }) {
  return (
    <>
      {points.map((point, index) => {
        const key = index;
        return point.kind === "x" ? (
          <g key={key}>
            <Bond point={point} />
            <Atom point={point} />
          </g>
        ) : (
          <LonePair key={key} point={point} />
        );
      })}
    </>
  );
}

/** A rotatable 3-D-style drawing of the central atom, bonded atoms and lone pairs. */
export function VseprSketch({
  points,
  description,
  strings,
}: {
  points: readonly SketchPoint[];
  /** Plain-text description for assistive technology. */
  description: string;
  strings: SketchStrings;
}) {
  const [azimuth, setAzimuth] = useState(START_AZIMUTH);
  const projected = projectPoints(points, azimuth, TILT);
  const behind = projected.filter((p) => p.depth < 0);
  const front = projected.filter((p) => p.depth >= 0);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.sketchHeading}</h3>
      <svg
        aria-label={t(strings.sketchLabel, { description })}
        className="mx-auto h-auto w-full max-w-xs rounded-lg border border-border bg-card"
        role="img"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
      >
        <Items points={behind} />
        <circle
          className="fill-foreground stroke-background"
          cx={CENTER}
          cy={CENTER}
          r={15}
          strokeWidth={2}
        />
        <text
          className="fill-background font-semibold"
          dominantBaseline="central"
          fontSize={14}
          textAnchor="middle"
          x={CENTER}
          y={CENTER}
        >
          A
        </text>
        <Items points={front} />
      </svg>
      <div className="mx-auto max-w-xs space-y-1">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="vsepr-rotate"
        >
          {strings.rotateLabel}
        </label>
        <input
          className="w-full accent-primary"
          id="vsepr-rotate"
          max={FULL_TURN}
          min={0}
          onChange={(event) => setAzimuth(Number(event.target.value))}
          type="range"
          value={azimuth}
        />
        <p className="text-muted-foreground text-xs">{strings.sketchKey}</p>
      </div>
    </div>
  );
}
